import React, { useState } from 'react';
import { View, Text, ScrollView, ActivityIndicator } from 'react-native';
import { isPhone, isWeb } from '@helpers';
import ColorWrapper, { WithColor } from '@styles/colors';
import TouchableElement from '@components/common/touchableElement';
import { scale } from '@components/frame';
import Loading, { SmallLoading } from '@components/loading';
import Svg from '@styles/icons/svg';
import { font } from '@styles/fonts';
import Header from '@components/settings/v2/common/SettingsHeader';
import i18n from 'i18n-js';
import { Operation } from '@interfaces/machines';
import { SCREEN_BORDER_RADIUS, TEST_IDS } from '@v2/pages/common/constants/base.constants';

import { useManageMachineAssignedOperations, SelectOperation } from './hooks/useManageMachineAssignedOperations';

const MachineAssignedOperations = ({ C }: WithColor) => {
  const {
    id,
    machine,
    loaded,
    visibleOperations,
    selectedList,
    loadingMore,
    elementWidth,
    handleScroll,
    selectOperation,
    onContainerLayout,
    onContentSizeChange,
  } = useManageMachineAssignedOperations();

  if (!loaded) {
    return <Loading background={C('Background/Light/Detail')} />;
  }

  if (!machine) {
    return null;
  }

  return (
    <View
      key="mainMachineContent"
      style={{
        height: '100%',
        width: '100%',
        backgroundColor: C('Background/Light/Detail'),
        alignItems: 'center',
      }}
    >
      <Header
        backBtnText="Machines"
        name={'MachinesOperationDetails'}
        header={machine.fullName || ''}
        action={undefined}
        subHeader={undefined}
        actionLoading={undefined}
        actionActive={undefined}
      />
      <ScrollView
        style={{ width: '100%', flex: 1 }}
        contentContainerStyle={{ paddingBottom: scale(50) }}
        onLayout={onContainerLayout}
        onContentSizeChange={onContentSizeChange}
        onScroll={handleScroll}
        scrollEventThrottle={16}
      >
        <View
          style={{
            paddingHorizontal: scale(18),
          }}
        >
          {machine.machineId ? (
            <View>
              <View style={{ marginBottom: scale(24) }}>
                <Text style={[font.Headline, { color: C('Label/Light/Primary') }]}>{i18n.t('Select the Operations linked to this Machine')}</Text>
              </View>
              <View style={{ width: '100%', flexDirection: 'row', flexWrap: 'wrap' }}>
                {visibleOperations.map((i: Operation, index: number) => (
                  <OperationBlock
                    key={`operation-${i.operationId}`}
                    {...{ i, index, C, elementWidth, selectedList, selectOperation }}
                  />
                ))}
              </View>
              {loadingMore && (
                <View style={{ padding: scale(16), alignItems: 'center', justifyContent: 'center' }}>
                  <ActivityIndicator size="small" color="#888" />
                </View>
              )}
            </View>
          ) : (
            <Loading background={C('Background/Light/Detail')} />
          )}
        </View>
      </ScrollView>
    </View>
  );
};

interface OperationBlockProps extends WithColor {
  i: Operation;
  index: number;
  selectedList: number[];
  elementWidth: number;
  selectOperation: (args: SelectOperation) => Promise<void>;
}

const OperationBlock = ({ i, selectedList, C, elementWidth, index, selectOperation }: OperationBlockProps) => {
  const [saving, setSaving] = useState(false);
  const isSelected = selectedList.includes(i.operationId);

  return (
    <TouchableElement
      testID={TEST_IDS.machineAssignedOperations.operationBlock(i.operationId)}
      onPress={async () => {
        if (saving) {
          return;
        }
        await selectOperation({ operation: i, selected: isSelected, setSaving });
      }}
    >
      {({ boxStyle, pressHoverColor }: any) => (
        <View
          style={[
            boxStyle,
            {
              width: isPhone ? '100%' : elementWidth,
              height: scale(48),
              borderRadius: scale(SCREEN_BORDER_RADIUS),
              backgroundColor: C(pressHoverColor('Background/Light/Field')),
              flexDirection: 'row',
              alignItems: 'center',
              paddingHorizontal: scale(12),
              borderWidth: scale(3),
              borderColor: isSelected ? C('Selected/Light/Primary') : C(pressHoverColor('Background/Light/Field')),
              marginBottom: isWeb ? scale(32) : scale(8),
              marginRight: isPhone ? 0 : scale((index + 1) % 3 > 0 ? 32 : 0),
            },
          ]}
        >
          <View style={{ width: scale(24), height: scale(24), justifyContent: 'center', alignContent: 'center' }}>
            {saving ? (
              <SmallLoading />
            ) : isSelected ? (
              <Svg icon="selected" width={scale(24)} height={scale(24)} color="Selected/Light/Primary" />
            ) : (
              <Svg icon="unselected" width={scale(24)} height={scale(24)} color="Label/Light/Quinary" />
            )}
          </View>
          <Text style={[font['Body'], { color: C('Label/Light/Primary'), marginLeft: scale(12) }]} numberOfLines={1}>
            {i.operationName}
          </Text>
        </View>
      )}
    </TouchableElement>
  );
};

export default ColorWrapper(MachineAssignedOperations);
